import { PageContainer } from "@/components/common/page-container";
import { routes } from "@/constants/url";
import { UnderlineTabs, UnderlineTabsContent, UnderlineTabsList, UnderlineTabsTrigger } from "@backpac/monica";
import { Suspense, useMemo } from "react";
import { useSearchParams } from "react-router";
import { ChannelList } from "./components/channel-list.tsx";
import { ChannelAnalytics } from "./components/channel/channel-analytics.tsx";
import { VideoAnalytics } from "./components/video/video-analytics.tsx";

export const DASHBOARD_TABS = {
  CHANNEL: "channel",
  CONTENTS: "contents",
} as const;

export type DashboardTab = (typeof DASHBOARD_TABS)[keyof typeof DASHBOARD_TABS];

export function useDashboardTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") as DashboardTab;
  const initialTab: DashboardTab = useMemo(
    () => (tab === DASHBOARD_TABS.CONTENTS ? DASHBOARD_TABS.CONTENTS : DASHBOARD_TABS.CHANNEL),
    [tab],
  );

  const setTab = (value: DashboardTab) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === DASHBOARD_TABS.CHANNEL) {
      newParams.delete("tab");
    } else {
      newParams.set("tab", value);
    }
    setSearchParams(newParams);
  };

  return {
    initialTab,
    setTab,
  };
}

export function DashboardPage() {
  const { initialTab, setTab } = useDashboardTab();

  return (
    <PageContainer
      nav={
        <PageContainer.Navigation
          title="Music"
          links={[
            { to: routes.MUSIC.root, text: "Library" },
            { to: routes.MUSIC.dashboard, text: "Dashboard" },
          ]}
        />
      }
    >
      <div className="mb-8">
        <Suspense fallback={<ChannelList.Pending />}>
          <ChannelList />
        </Suspense>
      </div>
      <UnderlineTabs value={initialTab} onValueChange={(value) => setTab(value as DashboardTab)}>
        <UnderlineTabsList>
          <UnderlineTabsTrigger value={DASHBOARD_TABS.CHANNEL}>Analytics</UnderlineTabsTrigger>
          <UnderlineTabsTrigger value={DASHBOARD_TABS.CONTENTS}>Contents</UnderlineTabsTrigger>
        </UnderlineTabsList>
        <UnderlineTabsContent value={DASHBOARD_TABS.CHANNEL} className="mt-4">
          <ChannelAnalytics />
        </UnderlineTabsContent>
        <UnderlineTabsContent value={DASHBOARD_TABS.CONTENTS} className="mt-4">
          <VideoAnalytics />
        </UnderlineTabsContent>
      </UnderlineTabs>
    </PageContainer>
  );
}
